/**
 * SnapshotHistoryItem - Individual Snapshot Row
 */

import React, { useState } from 'react';
import {
  FileText,
  Mail,
  ExternalLink,
  Check,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  FileJson
} from 'lucide-react';
import { useFormat } from '../../../i18n/format.js';

// Status badge config — `label` = repli FR, l'affichage passe par t('reports.snapshots.status.<code>')
const STATUS_CONFIG = {
  READY: {
    color: 'bg-green-100 text-green-800',
    icon: Check,
    label: 'Prêt'
  },
  PENDING: {
    color: 'bg-yellow-100 text-yellow-800',
    icon: Clock,
    label: 'En cours'
  },
  FAILED: {
    color: 'bg-red-100 text-red-800',
    icon: AlertCircle,
    label: 'Échec'
  }
};

// Format mappings (reserved for future format badges)
// eslint-disable-next-line no-unused-vars
const _FORMAT_ICONS = {
  csv: FileSpreadsheet,
  json: FileJson,
  pdf: FileText,
  xlsx: FileSpreadsheet
};

export default function SnapshotHistoryItem({
  snapshot,
  isLatest = false,
  onDownload,
  onEmail,
  onViewDetails
}) {
  const [expanded, setExpanded] = useState(false);

  const {
    id,
    as_of,
    record_count,
    status,
    filters_json,
    created_at
  } = snapshot;

  const { t, fmt } = useFormat();
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.READY;
  const statusLabel = t(`reports.snapshots.status.${STATUS_CONFIG[status] ? status : 'READY'}`, { defaultValue: statusConfig.label });
  const StatusIcon = statusConfig.icon;

  // Parse metadata from filters_json
  let metadata = {};
  try {
    if (filters_json) {
      const parsed = JSON.parse(filters_json);
      metadata = parsed._metadata || {};
    }
  } catch {
    // Ignore parse errors
  }

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return fmt.date(dateStr, 'long');
  };

  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return fmt.time(dateStr);
  };

  return (
    <div className={`bg-white border rounded-lg overflow-hidden transition-all ${
      isLatest ? 'border-blue-300 ring-2 ring-blue-100' : 'border-gray-200'
    }`}>
      {/* Main Row */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-4">
          {/* Date Column */}
          <div className="min-w-[140px]">
            <div className="font-medium text-gray-900">
              {formatDate(as_of)}
            </div>
            <div className="text-xs text-gray-500">
              {formatTime(created_at)}
            </div>
          </div>

          {/* Record Count */}
          <div className="flex items-center gap-2 text-gray-600">
            <FileText className="w-4 h-4 text-gray-400" />
            <span>
              <span className="font-medium">{record_count || 0}</span>
              <span className="text-gray-400 ml-1">{t('reports.snapshots.recordsAbbrev')}</span>
            </span>
          </div>

          {/* Status Badge */}
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}>
            <StatusIcon className="w-3.5 h-3.5" />
            {statusLabel}
          </span>

          {/* Latest Badge */}
          {isLatest && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
              <Check className="w-3 h-3" />
              {t('reports.snapshots.latest')}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Download Buttons */}
          {status === 'READY' && (
            <>
              <button
                onClick={() => onDownload?.(id, 'csv')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('reports.snapshots.downloadCsv')}
              >
                <FileSpreadsheet className="w-4 h-4" />
                CSV
              </button>
              <button
                onClick={() => onDownload?.(id, 'pdf')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('reports.snapshots.downloadPdf')}
              >
                <FileText className="w-4 h-4" />
                PDF
              </button>
            </>
          )}

          {/* Email Button */}
          <button
            onClick={() => onEmail?.(id)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title={t('reports.snapshots.sendEmail')}
          >
            <Mail className="w-4 h-4" />
          </button>

          {/* View Details */}
          <button
            onClick={() => onViewDetails?.(id)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('reports.snapshots.viewDetails')}
          >
            <ExternalLink className="w-4 h-4" />
          </button>

          {/* Expand/Collapse */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50/50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">ID:</span>
              <span className="ml-2 font-mono text-gray-700">{id}</span>
            </div>
            <div>
              <span className="text-gray-500">{t('reports.snapshots.generatedOn')}:</span>
              <span className="ml-2 text-gray-700">{formatDate(created_at)}</span>
            </div>
            {metadata.completeness && (
              <div>
                <span className="text-gray-500">{t('reports.snapshots.completeness')}:</span>
                <span className="ml-2 text-gray-700">{metadata.completeness.percentage}%</span>
              </div>
            )}
            {metadata.generated_by && (
              <div>
                <span className="text-gray-500">{t('reports.snapshots.source')}:</span>
                <span className="ml-2 text-gray-700">{metadata.generated_by}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
